// src/lib/storage.js - SUPABASE STORAGE (WORKING VERSION)

import { supabase } from './supabase'

const BUCKET = 'company_documents'

// ============================================================
// STORAGE LIMITS
// ============================================================

export const STORAGE_LIMITS = {
  MAX_FILE_SIZE: 10 * 1024 * 1024,
  MAX_FILE_SIZE_MB: 10,
  ALLOWED_TYPES: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'text/plain',
    'text/csv',
  ],
  ALLOWED_EXTENSIONS: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.jpg', '.jpeg', '.png', '.gif', '.webp', '.txt', '.csv'],
}

// ============================================================
// MAGIC BYTES VALIDATION
// ============================================================

const MAGIC_BYTES = {
  pdf: [0x25, 0x50, 0x44, 0x46],
  jpeg: [0xFF, 0xD8, 0xFF],
  png: [0x89, 0x50, 0x4E, 0x47],
  gif: [0x47, 0x49, 0x46, 0x38],
}

function validateMagicBytes(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const bytes = new Uint8Array(e.target.result)
      let isValid = false
      for (const [type, signature] of Object.entries(MAGIC_BYTES)) {
        let match = true
        for (let i = 0; i < signature.length; i++) {
          if (bytes[i] !== signature[i]) { match = false; break }
        }
        if (match) { isValid = true; break }
      }
      if (!isValid) {
        const isText = bytes.every(b => b >= 32 && b <= 126 || b === 10 || b === 13)
        if (isText) isValid = true
      }
      resolve(isValid)
    }
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsArrayBuffer(file.slice(0, 16))
  })
}

// ============================================================
// FILE VALIDATION
// ============================================================

async function validateFile(file) {
  if (!file) throw new Error('No file provided')
  if (file.size > STORAGE_LIMITS.MAX_FILE_SIZE) {
    throw new Error(`File size exceeds ${STORAGE_LIMITS.MAX_FILE_SIZE_MB}MB limit`)
  }
  if (file.size === 0) throw new Error('File is empty')

  const fileExt = '.' + file.name.split('.').pop().toLowerCase()
  if (!STORAGE_LIMITS.ALLOWED_EXTENSIONS.includes(fileExt)) {
    throw new Error(`File type ${fileExt} is not allowed. Allowed: ${STORAGE_LIMITS.ALLOWED_EXTENSIONS.join(', ')}`)
  }

  // Check MIME type
  if (!STORAGE_LIMITS.ALLOWED_TYPES.includes(file.type)) {
    throw new Error(`File type ${file.type} is not allowed`)
  }

  const dangerousPatterns = ['..', '/', '\\', '%00', ';', '|', '&', '$', '(', ')', '`']
  for (const pattern of dangerousPatterns) {
    if (file.name.includes(pattern)) throw new Error('File name contains invalid characters')
  }

  // ✅ Validate magic bytes
  const isValidMagic = await validateMagicBytes(file)
  if (!isValidMagic) throw new Error('Invalid file format or corrupted file')

  // ✅ Validate file extension matches content type
  const ext = file.name.split('.').pop().toLowerCase()
  const mimeMap = {
    pdf: ['application/pdf'],
    jpg: ['image/jpeg'],
    jpeg: ['image/jpeg'],
    png: ['image/png'],
    gif: ['image/gif'],
    doc: ['application/msword'],
    docx: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    xls: ['application/vnd.ms-excel'],
    xlsx: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  }
  
  const allowedMimes = mimeMap[ext] || []
  if (allowedMimes.length > 0 && !allowedMimes.includes(file.type)) {
    throw new Error(`File extension .${ext} doesn't match content type ${file.type}`)
  }

  return true
}

// ============================================================
// UPLOAD FILE
// ============================================================

export async function uploadFile(file, companyId, category, fileName) {
  await validateFile(file)

  const sanitizedName = (fileName || file.name)
    .replace(/[^a-zA-Z0-9.\s-]/g, '')
    .replace(/\s+/g, '-')
    .toLowerCase()
    .substring(0, 200)

  const timestamp = Date.now()
  const path = `${companyId}/${category}/${timestamp}-${sanitizedName}`

  const { data, error } = await supabase.storage
    .from(BUCKET)
    .upload(path, file, { 
      upsert: false,
      contentType: file.type,
      cacheControl: '3600',
    })

  if (error) {
    if (error.message.includes('duplicate')) {
      throw new Error('A file with this name already exists')
    }
    if (error.message.includes('permission')) {
      throw new Error('You do not have permission to upload files')
    }
    if (error.message.includes('size')) {
      throw new Error(`File size exceeds the ${STORAGE_LIMITS.MAX_FILE_SIZE_MB}MB limit`)
    }
    throw error
  }

  return { 
    path: data.path, 
    fullPath: path,
    fileName: sanitizedName,
    uploadedAt: new Date().toISOString(),
  }
}

// ============================================================
// GET SIGNED URL
// ============================================================

export async function getSignedUrl(companyId, category, fileName, expirySeconds = 60) {
  const path = `${companyId}/${category}/${fileName}`
  return getSignedUrlByPath(path, expirySeconds)
}

export async function getSignedUrlByPath(path, expirySeconds = 60) {
  if (path.includes('..') || path.includes('//')) {
    throw new Error('Invalid file path')
  }

  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(path, expirySeconds)

  if (error) throw error
  return data?.signedUrl || null
}

export async function getMultipleSignedUrls(paths, expirySeconds = 60) {
  const results = {}
  for (const path of paths) {
    try {
      results[path] = await getSignedUrlByPath(path, expirySeconds)
    } catch {
      results[path] = null
    }
  }
  return results
}

// ============================================================
// DELETE FILE
// ============================================================

export async function deleteFile(path) {
  if (path.includes('..') || path.includes('//')) {
    throw new Error('Invalid file path')
  }

  const { error } = await supabase.storage
    .from(BUCKET)
    .remove([path])

  if (error) throw error
  return true
}

// ============================================================
// LIST FILES
// ============================================================

export async function listFiles(companyId, options = {}) {
  const { limit = 100, sortBy = { column: 'created_at', order: 'desc' } } = options

  const { data, error } = await supabase.storage
    .from(BUCKET)
    .list(companyId, { 
      limit, 
      sortBy,
      ...(options.recursive ? { recursive: true } : {}),
    })

  if (error) throw error
  return data || []
}

// ============================================================
// FILE INFO & EXISTS
// ============================================================

export async function getFileInfo(path) {
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .info(path)

  if (error) throw error
  return data
}

export async function fileExists(path) {
  try {
    const info = await getFileInfo(path)
    return !!info
  } catch {
    return false
  }
}

// ============================================================
// EXPORT
// ============================================================

export default {
  STORAGE_LIMITS,
  uploadFile,
  getSignedUrl,
  getSignedUrlByPath,
  getMultipleSignedUrls,
  deleteFile,
  listFiles,
  getFileInfo,
  fileExists,
}